import type {
  MetricComparison,
  MetricComparisonKind,
  MetricComparisonResponse,
  MetricComparisonRow,
  ServerApiClient,
} from '../data/ServerApiClient'
import type { AppWorkerClient } from '../data/AppWorkerClient'
import type { CfoAgentToolHandlers } from './cfoAgent'
import { buildCfoDrilldownArtifactReadiness, runCfoDrilldownRecipe } from './cfoDrilldownRecipes'

// Phase 0 of the CFO Core AI tooling plan (docs/cfo-core/AI_TOOLING_PLAN.md §2.6):
// make the cfo-home Brief copilot answer with real data.
//
// The Brief mounts the shared `useCfoAgent` loop, but the server planner
// (`server/src/ai_agent.rs::plan_initial_tool_calls`) schedules *surface*
// tools for questions like "Why did profit change this month?":
// `list_agent_artifacts`, `get_metric_definition`, `get_metric_trend`,
// `explain_metric_change`, `get_statement_detail`. Without handlers those calls
// fall through to the bridge `default` branch and return "not implemented", so
// the model reasons over an all-failed evidence set and truthfully reports "no
// data". This module binds those tools to the already-warmed local CFO artifact
// views in browser DuckDB. If the local views are absent, the handlers return a
// structured not-ready result instead of hiding the cache gap behind a server
// data fetch.
//
// Growth Analysis (`QuickBooksGrowthAnalysisWorkspace.tsx`) binds the same tool
// names to its in-memory analysis state (`treeNodes`, `node.trend`,
// `serverAnalysis`, `projected`). Those data sources are fundamentally
// different from the Brief's local CFO semantic views, so this bundle is built
// for the Brief rather than shared with Growth in this pass.

const trimStr = (value: unknown): string => String(value ?? '').trim()

const COMPARISON_KINDS: ReadonlySet<MetricComparisonKind> = new Set([
  'mom',
  'qoq',
  'yoy',
  'ytd',
  'l3m_vs_prior3m',
  'l12m_vs_prior12m',
])

const COMPARISON_ALIASES: Record<string, MetricComparisonKind> = {
  month_over_month: 'mom',
  prior_month: 'mom',
  mom: 'mom',
  quarter_over_quarter: 'qoq',
  prior_quarter: 'qoq',
  qoq: 'qoq',
  year_over_year: 'yoy',
  prior_year: 'yoy',
  yoy: 'yoy',
  year_to_date: 'ytd',
  ytd: 'ytd',
  last_3_months: 'l3m_vs_prior3m',
  last_3mo: 'l3m_vs_prior3m',
  l3m: 'l3m_vs_prior3m',
  l3m_vs_prior3m: 'l3m_vs_prior3m',
  last_12_months: 'l12m_vs_prior12m',
  last_12mo: 'l12m_vs_prior12m',
  l12m: 'l12m_vs_prior12m',
  l12m_vs_prior12m: 'l12m_vs_prior12m',
}

const normalizeComparisonKind = (value: unknown): MetricComparisonKind | null => {
  const text = trimStr(value).toLowerCase()
  if (!text) return null
  if (COMPARISON_KINDS.has(text as MetricComparisonKind)) return text as MetricComparisonKind
  return COMPARISON_ALIASES[text] || null
}

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item) => trimStr(item)).filter(Boolean)
  const single = trimStr(value)
  return single ? [single] : []
}

const requestedComparisonKind = (args: Record<string, any>, fallback: MetricComparisonKind = 'mom'): MetricComparisonKind => {
  const tokens = [
    ...toStringArray(args.comparison ?? args.comparison_kind ?? args.comparisonKind),
    ...toStringArray(args.kind),
    ...toStringArray(args.kinds),
    ...toStringArray(args.comparisons),
  ]
  for (const token of tokens) {
    const kind = normalizeComparisonKind(token)
    if (kind) return kind
  }
  return fallback
}

const requestedComparisonKinds = (args: Record<string, any>): MetricComparisonKind[] => {
  const tokens = [
    ...toStringArray(args.comparison ?? args.comparison_kind ?? args.comparisonKind),
    ...toStringArray(args.kind),
    ...toStringArray(args.kinds),
    ...toStringArray(args.comparisons),
  ]
  const seen = new Set<MetricComparisonKind>()
  for (const token of tokens) {
    const kind = normalizeComparisonKind(token)
    if (kind) seen.add(kind)
  }
  return Array.from(seen)
}

// Light noun -> registry-id aliases so a placeholder/colloquial metric token
// ("profit", the planner's "selected") resolves to the canonical id the Brief
// renders. Intentionally tiny: Phase 1 moves real noun->metric inference into
// the server planner against the metric registry (plan §2.7); this only nudges
// matching within already-loaded rows and never invents data.
const METRIC_ALIASES: Record<string, string> = {
  selected: '',
  profit: 'net_income',
  profits: 'net_income',
  income: 'net_income',
  earnings: 'net_income',
  margin: 'gross_margin',
  sales: 'revenue',
  topline: 'revenue',
  cost: 'cost_of_sales',
  costs: 'cost_of_sales',
  cogs: 'cost_of_sales',
  cost_of_goods_sold: 'cost_of_sales',
  cost_of_goods: 'cost_of_sales',
  opex: 'operating_expenses',
}

/** Pull the requested metric id(s) from a tool call's arguments. */
const requestedMetricIds = (args: Record<string, any>): string[] => {
  const raw = toStringArray(
    args.metric_id ?? args.metricId ?? args.metric ?? args.metrics ?? args.metric_ids ?? args.metricIds,
  )
  const resolved: string[] = []
  for (const token of raw) {
    const alias = METRIC_ALIASES[token.toLowerCase()]
    const id = alias === undefined ? token : alias
    if (id) resolved.push(id)
  }
  return resolved
}

/** Find a metric row tolerant of registry id drift (mirrors CfoHomeBrief). */
const findMetricRow = (resp: MetricComparisonResponse | null, id: string): MetricComparisonRow | null => {
  if (!resp) return null
  const exact = resp.metrics.find((m) => m.id === id)
  if (exact) return exact
  const lowered = id.toLowerCase()
  return (
    resp.metrics.find(
      (m) => m.id.toLowerCase() === lowered || m.label.toLowerCase().includes(lowered),
    ) || null
  )
}

const pickComparison = (row: MetricComparisonRow | null, kind: MetricComparisonKind): MetricComparison | null =>
  row?.comparisons.find((c) => c.kind === kind) || null

const buildTrend = (row: MetricComparisonRow | null): { labels: string[]; trend: number[] } => {
  if (!row) return { labels: [], trend: [] }
  const monthly = Array.isArray(row.trend)
    ? row.trend
      .filter((point) => point?.value != null && Number.isFinite(point.value))
      .slice(-12)
    : []
  if (monthly.length) {
    return {
      labels: monthly.map((point) => point.label || point.period),
      trend: monthly.map((point) => Number(point.value)),
    }
  }
  const ordered: Array<{ label: string; value: number | null }> = [
    { label: 'YoY prior', value: pickComparison(row, 'yoy')?.prior_value ?? null },
    { label: 'L12M prior', value: pickComparison(row, 'l12m_vs_prior12m')?.prior_value ?? null },
    { label: 'QoQ prior', value: pickComparison(row, 'qoq')?.prior_value ?? null },
    { label: 'L3M prior', value: pickComparison(row, 'l3m_vs_prior3m')?.prior_value ?? null },
    { label: 'MoM prior', value: pickComparison(row, 'mom')?.prior_value ?? null },
    { label: 'Latest', value: row.latest_value ?? null },
  ]
  const labels: string[] = []
  const trend: number[] = []
  for (const point of ordered) {
    if (point.value != null && Number.isFinite(point.value)) {
      labels.push(point.label)
      trend.push(point.value)
    }
  }
  return { labels, trend }
}

const summarizeMovement = (comparison: MetricComparison | null) => {
  if (!comparison) return null
  return {
    kind: comparison.kind,
    label: comparison.label,
    current_value: comparison.current_value,
    prior_value: comparison.prior_value,
    delta_abs: comparison.delta_abs,
    delta_pct: comparison.delta_pct,
    current_pct_of_sales: comparison.current_pct_of_sales,
    prior_pct_of_sales: comparison.prior_pct_of_sales,
    delta_pct_of_sales_pts: comparison.delta_pct_of_sales_pts,
    complete: comparison.complete,
  }
}

const isExpenseLikeMetric = (row: MetricComparisonRow): boolean => {
  const text = `${row.id} ${row.label}`.toLowerCase()
  return /(expense|cost|cogs|payroll|wage|salary|rent|interest|tax|depreciation|amortization|insurance|utilities|meals|travel|fees|dues|supplies)/.test(text)
}

const buildProfitDrivers = (
  resp: MetricComparisonResponse,
  kind: MetricComparisonKind = 'mom',
): Array<Record<string, any>> => {
  return resp.metrics
    .filter((metric) => metric.statement === 'profit_and_loss' && metric.value_kind === 'currency' && metric.id !== 'net_income')
    .map((metric) => {
      const movement = pickComparison(metric, kind)
      if (!movement || movement.delta_abs == null || !Number.isFinite(movement.delta_abs)) return null
      const expenseLike = isExpenseLikeMetric(metric)
      const profitEffect = expenseLike ? -movement.delta_abs : movement.delta_abs
      return {
        metric_id: metric.id,
        label: metric.label,
        line_type: expenseLike ? 'expense_or_cost' : 'income_or_margin',
        comparison: kind,
        current_value: movement.current_value,
        prior_value: movement.prior_value,
        delta_abs: movement.delta_abs,
        delta_pct: movement.delta_pct,
        current_pct_of_sales: movement.current_pct_of_sales,
        prior_pct_of_sales: movement.prior_pct_of_sales,
        profit_effect: profitEffect,
        direction: profitEffect >= 0 ? 'helped_profit' : 'hurt_profit',
      }
    })
    .filter((row): row is NonNullable<typeof row> => row != null)
    .sort((a, b) => Math.abs(Number(b.profit_effect || 0)) - Math.abs(Number(a.profit_effect || 0)))
    .slice(0, 8)
}

const CFO_AI_TABLES = {
  statementMonthlyActivity: 'fg_cfo_ai_statement_monthly_activity',
  opexMonthlyActivity: 'fg_cfo_ai_opex_monthly_activity',
  glDetail: 'fg_cfo_ai_gl_detail',
} as const

const LOCAL_METRIC_DEFS: ReadonlyArray<{
  id: string
  label: string
  statement: MetricComparisonRow['statement']
  value_kind: MetricComparisonRow['value_kind']
  formula?: string
  interpretation?: string
}> = [
  { id: 'revenue', label: 'Revenue', statement: 'profit_and_loss', value_kind: 'currency' },
  { id: 'gross_profit', label: 'Gross Profit', statement: 'profit_and_loss', value_kind: 'currency', formula: 'Revenue - Cost of Goods Sold' },
  { id: 'cost_of_sales', label: 'Cost of Goods Sold', statement: 'profit_and_loss', value_kind: 'currency', formula: 'Revenue - Gross Profit' },
  { id: 'operating_expenses', label: 'Operating Expenses', statement: 'profit_and_loss', value_kind: 'currency' },
  { id: 'net_income', label: 'Net Income', statement: 'profit_and_loss', value_kind: 'currency', formula: 'Revenue - COGS - operating expenses, plus/minus other P&L activity' },
  { id: 'gross_margin', label: 'Gross Margin', statement: 'profit_and_loss', value_kind: 'ratio', formula: 'Gross Profit / Revenue', interpretation: 'Share of revenue left after cost of goods sold.' },
  { id: 'net_margin', label: 'Net Margin', statement: 'profit_and_loss', value_kind: 'ratio', formula: 'Net Income / Revenue', interpretation: 'Share of revenue that becomes net income.' },
]

const LOCAL_METRIC_DEF_BY_ID = new Map(LOCAL_METRIC_DEFS.map((def) => [def.id, def]))

const quoteDuckIdent = (value: string): string => `"${String(value).replace(/"/g, '""')}"`
const quoteDuckString = (value: string): string => `'${String(value).replace(/'/g, "''")}'`

const previewRowsAsObjects = (result: any): Array<Record<string, any>> => {
  const headers: string[] = Array.isArray(result?.headers) ? result.headers.map((h: any) => String(h || '')) : []
  const rows = Array.isArray(result?.preview_rows) ? result.preview_rows : (Array.isArray(result?.rows) ? result.rows : [])
  if (!headers.length) {
    return rows.map((row: any) => (row && typeof row === 'object' && !Array.isArray(row)) ? row : {})
  }
  return rows.map((row: any) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) return row
    const values = Array.isArray(row) ? row : []
    const out: Record<string, any> = {}
    headers.forEach((header, idx) => {
      out[header] = values[idx]
    })
    return out
  })
}

const num = (value: unknown, fallback = 0): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const boundedEvidenceLimit = (value: unknown, fallback = 25, max = 50): number => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(max, Math.max(1, Math.floor(parsed)))
}

const cfoContractSourceArtifactIds = (contract: any): string[] =>
  Array.isArray(contract?.sources)
    ? contract.sources.flatMap((source: any) => Array.isArray(source?.artifact_ids) ? source.artifact_ids.map((id: any) => trimStr(id)).filter(Boolean) : [])
    : []

const cfoContractEvidenceMetadata = (contract: any, source: string): Record<string, any> => ({
  source,
  freshness_token: contract?.freshness_token ?? null,
  readiness: contract?.readiness ?? null,
  rebuild_state: contract?.rebuild_state ?? null,
  generated_at: contract?.generated_at ?? null,
  row_count: Number.isFinite(Number(contract?.row_count)) ? Number(contract.row_count) : null,
  warnings: Array.isArray(contract?.warnings) ? contract.warnings : [],
  source_artifact_ids: cfoContractSourceArtifactIds(contract),
  sources: Array.isArray(contract?.sources) ? contract.sources : [],
})

const normalizeMonthStart = (value: unknown): string => {
  const text = trimStr(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return `${text.slice(0, 7)}-01`
  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return ''
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const addMonths = (monthStart: string, delta: number): string => {
  const parsed = new Date(`${normalizeMonthStart(monthStart)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return ''
  parsed.setUTCMonth(parsed.getUTCMonth() + delta)
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const comparisonLabel = (kind: MetricComparisonKind): string => {
  switch (kind) {
    case 'mom':
      return 'Month over Month'
    case 'qoq':
      return 'Quarter over Quarter'
    case 'yoy':
      return 'Year over Year'
    case 'ytd':
      return 'Year to Date'
    case 'l3m_vs_prior3m':
      return 'Last 3 vs Prior 3 Months'
    case 'l12m_vs_prior12m':
      return 'Last 12 vs Prior 12 Months'
    default:
      return kind
  }
}

type LocalMetricMonthlyRow = {
  period_start: string
  connection_id: string
  workbook_id: string
  revenue: number
  cost_of_sales: number
  operating_expenses: number
  other_income: number
  other_expense: number
  gross_profit: number
  net_income: number
  gross_margin: number | null
  net_margin: number | null
}

type MonthWindow = { start: number; end: number }

const metricValueForLocalRow = (row: LocalMetricMonthlyRow | null | undefined, metricId: string): number | null => {
  if (!row) return null
  const value = (row as any)[metricId]
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const localMetricIsRatio = (metricId: string): boolean =>
  LOCAL_METRIC_DEF_BY_ID.get(metricId)?.value_kind === 'ratio'

const localMetricWindowValue = (
  rows: LocalMetricMonthlyRow[],
  window: MonthWindow,
  metricId: string,
): { value: number | null; complete: boolean; revenue: number | null } => {
  let observed = 0
  let value = 0
  let revenue = 0
  if (localMetricIsRatio(metricId)) {
    const row = rows[window.end] || null
    return {
      value: metricValueForLocalRow(row, metricId),
      complete: Boolean(row),
      revenue: metricValueForLocalRow(row, 'revenue'),
    }
  }
  for (let idx = window.start; idx <= window.end; idx += 1) {
    const row = rows[idx]
    const rowValue = metricValueForLocalRow(row, metricId)
    if (row && rowValue != null) {
      observed += 1
      value += rowValue
      revenue += metricValueForLocalRow(row, 'revenue') || 0
    }
  }
  return {
    value: observed ? Math.round(value * 100) / 100 : null,
    complete: observed === window.end - window.start + 1,
    revenue: observed ? Math.round(revenue * 100) / 100 : null,
  }
}

const localComparisonWindow = (
  kind: MetricComparisonKind,
  latestIdx: number,
  latestMonth: number,
): { current: MonthWindow; prior: MonthWindow } | null => {
  const win = (start: number, end: number): MonthWindow | null =>
    start < 0 || end < start ? null : { start, end }
  switch (kind) {
    case 'mom': {
      const current = win(latestIdx, latestIdx)
      const prior = win(latestIdx - 1, latestIdx - 1)
      return current && prior ? { current, prior } : null
    }
    case 'yoy': {
      const current = win(latestIdx, latestIdx)
      const prior = win(latestIdx - 12, latestIdx - 12)
      return current && prior ? { current, prior } : null
    }
    case 'ytd': {
      const currentStart = latestIdx - latestMonth + 1
      const current = win(currentStart, latestIdx)
      const prior = win(currentStart - 12, latestIdx - 12)
      return current && prior ? { current, prior } : null
    }
    case 'l3m_vs_prior3m': {
      const current = win(latestIdx - 2, latestIdx)
      const prior = win(latestIdx - 5, latestIdx - 3)
      return current && prior ? { current, prior } : null
    }
    case 'l12m_vs_prior12m': {
      const current = win(latestIdx - 11, latestIdx)
      const prior = win(latestIdx - 23, latestIdx - 12)
      return current && prior ? { current, prior } : null
    }
    case 'qoq': {
      const monthsIntoQuarter = ((latestMonth - 1) % 3) + 1
      const quarterEnd = latestIdx - (monthsIntoQuarter % 3)
      const current = win(quarterEnd - 2, quarterEnd)
      const prior = win(quarterEnd - 5, quarterEnd - 3)
      return current && prior ? { current, prior } : null
    }
    default:
      return null
  }
}

const comparisonPctOfSales = (value: number | null, revenue: number | null): number | null => {
  if (value == null || revenue == null || Math.abs(revenue) <= 0.000001) return null
  return value / revenue
}

const localComparisonForMetric = (
  rows: LocalMetricMonthlyRow[],
  metricId: string,
  kind: MetricComparisonKind,
): MetricComparison | null => {
  const latestIdx = rows.length - 1
  if (latestIdx < 0) return null
  const latestMonth = Number(rows[latestIdx].period_start.slice(5, 7))
  const windows = localComparisonWindow(kind, latestIdx, latestMonth)
  if (!windows) return null
  const current = localMetricWindowValue(rows, windows.current, metricId)
  const prior = localMetricWindowValue(rows, windows.prior, metricId)
  const currentValue = current.value
  const priorValue = prior.value
  const deltaAbs = currentValue != null && priorValue != null ? Math.round((currentValue - priorValue) * 100) / 100 : null
  const deltaPct =
    currentValue != null && priorValue != null && Math.abs(priorValue) > 0.000001
      ? currentValue / priorValue - 1
      : null
  const currentPct = LOCAL_METRIC_DEF_BY_ID.get(metricId)?.value_kind === 'currency'
    ? comparisonPctOfSales(currentValue, current.revenue)
    : null
  const priorPct = LOCAL_METRIC_DEF_BY_ID.get(metricId)?.value_kind === 'currency'
    ? comparisonPctOfSales(priorValue, prior.revenue)
    : null
  return {
    kind,
    label: comparisonLabel(kind),
    current_period_start: rows[windows.current.start]?.period_start ?? null,
    current_period_end: rows[windows.current.end]?.period_start ?? null,
    prior_period_start: rows[windows.prior.start]?.period_start ?? null,
    prior_period_end: rows[windows.prior.end]?.period_start ?? null,
    current_value: currentValue,
    prior_value: priorValue,
    delta_abs: deltaAbs,
    delta_pct: deltaPct,
    current_pct_of_sales: currentPct,
    prior_pct_of_sales: priorPct,
    delta_pct_of_sales_pts: currentPct != null && priorPct != null ? currentPct - priorPct : null,
    complete: current.complete && prior.complete,
  }
}

type LocalComparisonResolution =
  | { status: 'ready'; response: MetricComparisonResponse }
  | { status: 'not_ready'; result: Record<string, any> }

const runDuckRows = async (
  workerClient: AppWorkerClient,
  sql: string,
  opts: { previewRowLimit?: number; timeoutMs?: number; caller: string },
  duckDbOpts?: Record<string, any> | null,
): Promise<Array<Record<string, any>>> => {
  const result = await workerClient.duckdb_run_sql(sql, {
    ...(duckDbOpts || {}),
    previewRowLimit: opts.previewRowLimit ?? 1000,
    timeoutMs: opts.timeoutMs ?? 60000,
    allowMutating: false,
    cacheExtracts: true,
    caller: opts.caller.startsWith('QuickBooksCfoCore:')
      ? opts.caller
      : `QuickBooksCfoCore:${opts.caller}`,
  })
  return previewRowsAsObjects(result)
}

const duckTableExistsUncached = async (
  workerClient: AppWorkerClient,
  tableName: string,
  duckDbOpts?: Record<string, any> | null,
): Promise<boolean> => {
  const rows = await runDuckRows(
    workerClient,
    `
WITH local_objects AS (
  SELECT table_name FROM information_schema.tables
  UNION ALL
  SELECT table_name FROM information_schema.views
)
SELECT COUNT(*) AS n
FROM local_objects
WHERE lower(table_name) = lower(${quoteDuckString(tableName)})`,
    { previewRowLimit: 1, timeoutMs: 15000, caller: `CfoBriefToolHandlers:table_exists:${tableName}` },
    duckDbOpts,
  )
  return num(rows[0]?.n) > 0
}

// Readiness cache for warmed CFO artifacts, keyed by the worker (DuckDB
// instance) then by execution scope + table. A `true` result is permanent (a
// view never un-creates); a `false` is cached only briefly because background
// warming may finish mid-session. Caching the in-flight promise dedupes the
// dozens of identical probes a single agent run otherwise fires (the reported
// session ran `table_exists` ~30× for one table). Keying by worker instance also
// keeps each DuckDB instance — and each test's mock — independent.
type CfoArtifactReadiness = { value: boolean; expiresAt: number }
const CFO_ARTIFACT_NEGATIVE_TTL_MS = 1500
const cfoArtifactReadinessByWorker = new WeakMap<object, Map<string, CfoArtifactReadiness>>()
const cfoArtifactReadinessInflightByWorker = new WeakMap<object, Map<string, Promise<boolean>>>()

const cfoArtifactReadinessMaps = (workerClient: object) => {
  let cache = cfoArtifactReadinessByWorker.get(workerClient)
  if (!cache) {
    cache = new Map()
    cfoArtifactReadinessByWorker.set(workerClient, cache)
  }
  let inflight = cfoArtifactReadinessInflightByWorker.get(workerClient)
  if (!inflight) {
    inflight = new Map()
    cfoArtifactReadinessInflightByWorker.set(workerClient, inflight)
  }
  return { cache, inflight }
}

const cfoArtifactReadinessKey = (duckDbOpts: Record<string, any> | null | undefined, tableName: string): string => {
  const scope = trimStr(duckDbOpts?.executionScopeId) || trimStr(duckDbOpts?.persistentDatasetId) || 'default'
  return `${scope}::${tableName.toLowerCase()}`
}

/** Mark a warmed artifact ready (called by the warmer the instant a view exists). */
export const markCfoArtifactReady = (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  tableName: string,
): void => {
  if (!workerClient) return
  const { cache } = cfoArtifactReadinessMaps(workerClient)
  cache.set(cfoArtifactReadinessKey(duckDbOpts, tableName), {
    value: true,
    expiresAt: Number.POSITIVE_INFINITY,
  })
}

const duckTableExists = async (
  workerClient: AppWorkerClient,
  tableName: string,
  duckDbOpts?: Record<string, any> | null,
): Promise<boolean> => {
  const { cache, inflight } = cfoArtifactReadinessMaps(workerClient)
  const key = cfoArtifactReadinessKey(duckDbOpts, tableName)
  const cached = cache.get(key)
  if (cached && (cached.value || cached.expiresAt > Date.now())) return cached.value
  const existing = inflight.get(key)
  if (existing) return existing
  const probe = duckTableExistsUncached(workerClient, tableName, duckDbOpts)
    .then((exists) => {
      cache.set(key, {
        value: exists,
        expiresAt: exists ? Number.POSITIVE_INFINITY : Date.now() + CFO_ARTIFACT_NEGATIVE_TTL_MS,
      })
      return exists
    })
    .finally(() => {
      inflight.delete(key)
    })
  inflight.set(key, probe)
  return probe
}

/**
 * Wait (bounded) for a warmed artifact to become ready, instead of failing the
 * instant a deeper drilldown lands a beat before warming finishes. Polls past
 * the negative cache; never used on the grounded first-answer path.
 */
export const awaitCfoArtifactReady = async (
  workerClient: AppWorkerClient | null | undefined,
  tableName: string,
  duckDbOpts?: Record<string, any> | null,
  opts?: { timeoutMs?: number },
): Promise<boolean> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return false
  if (await duckTableExists(workerClient, tableName, duckDbOpts)) return true
  const { cache } = cfoArtifactReadinessMaps(workerClient)
  const key = cfoArtifactReadinessKey(duckDbOpts, tableName)
  const deadline = Date.now() + Math.max(0, opts?.timeoutMs ?? 2500)
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 300))
    const exists = await duckTableExistsUncached(workerClient, tableName, duckDbOpts)
    if (exists) {
      cache.set(key, { value: true, expiresAt: Number.POSITIVE_INFINITY })
      return true
    }
  }
  return false
}

const localArtifactsNotReady = (summary: string): Record<string, any> => ({
  status: 'not_ready',
  summary,
  missing_tables: [CFO_AI_TABLES.statementMonthlyActivity],
  semantic_gap: {
    required: [CFO_AI_TABLES.statementMonthlyActivity],
    message: 'CFO analysis tools read from warmed local CFO artifacts. Run CFO Home warmup or Update QuickBooks data before asking the agent for workbook evidence.',
  },
  freshness: { source: 'local_duckdb', required_tables: [CFO_AI_TABLES.statementMonthlyActivity] },
})

export const buildLocalMetricComparisons = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  workbookId: string | null | undefined,
  args: Record<string, any>,
  metricIds: string[],
): Promise<LocalComparisonResolution> => {
  const wb = trimStr(workbookId)
  if (!wb) {
    return {
      status: 'not_ready',
      result: {
        status: 'not_ready',
        summary: 'No active workbook id is available for local CFO analysis.',
        freshness: { source: 'local_duckdb' },
      },
    }
  }
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    return {
      status: 'not_ready',
      result: localArtifactsNotReady('Local DuckDB is not available yet, so CFO analysis evidence cannot be computed.'),
    }
  }
  const ready = await duckTableExists(workerClient, CFO_AI_TABLES.statementMonthlyActivity, duckDbOpts)
  if (!ready) {
    return {
      status: 'not_ready',
      result: localArtifactsNotReady('CFO statement monthly activity is not warmed locally yet, so the agent will not call the server for replacement evidence.'),
    }
  }

  const requestedKinds = [
    ...toStringArray(args.comparison ?? args.comparison_kind ?? args.comparisonKind),
    ...toStringArray(args.comparisons ?? args.kinds ?? args.kind),
  ]
    .map(normalizeComparisonKind)
    .filter((kind): kind is MetricComparisonKind => kind != null)
  const comparisons = requestedKinds.length ? requestedKinds : Array.from(COMPARISON_KINDS)
  const asOf = normalizeMonthStart(args.as_of ?? args.asOf)
  const metricFilter = new Set(metricIds.filter((id) => LOCAL_METRIC_DEF_BY_ID.has(id)))
  const selectedDefs = LOCAL_METRIC_DEFS.filter((def) => !metricFilter.size || metricFilter.has(def.id))

  const rows = await runDuckRows(
    workerClient,
    `
WITH scoped AS (
  SELECT
    CAST(date_trunc('month', TRY_CAST(period_start AS DATE)) AS VARCHAR) AS period_start,
    nullif(trim(CAST(connection_id AS VARCHAR)), '') AS connection_id,
    nullif(trim(CAST(workbook_id AS VARCHAR)), '') AS workbook_id,
    lower(concat_ws(' ',
      coalesce(statement_type, ''),
      coalesce(account_class, ''),
      coalesce(account_type, ''),
      coalesce(category_label, ''),
      coalesce(account_breadcrumb, ''),
      coalesce(account_label, '')
    )) AS line_text,
    coalesce(TRY_CAST(amount AS DOUBLE), TRY_CAST(monthly_activity AS DOUBLE), 0) AS raw_amount,
    lower(coalesce(normal_balance, '')) AS normal_balance
  FROM ${quoteDuckIdent(CFO_AI_TABLES.statementMonthlyActivity)}
  WHERE TRY_CAST(period_start AS DATE) IS NOT NULL
    ${asOf ? `AND TRY_CAST(period_start AS DATE) <= TRY_CAST(${quoteDuckString(asOf)} AS DATE)` : ''}
),
typed AS (
  SELECT
    period_start,
    connection_id,
    workbook_id,
    CASE WHEN normal_balance = 'credit' THEN -raw_amount ELSE raw_amount END AS signed_amount,
    CASE
      WHEN line_text LIKE '%other income%' OR line_text LIKE '%otherincome%' THEN 'other_income'
      WHEN line_text LIKE '%other expense%' OR line_text LIKE '%otherexpense%' THEN 'other_expense'
      WHEN line_text LIKE '%cost of goods%' OR line_text LIKE '%cost of sales%' OR line_text LIKE '%costofgoodssold%' OR line_text LIKE '%cogs%' THEN 'cost_of_sales'
      WHEN line_text LIKE '%revenue%' OR line_text LIKE '%income%' OR line_text LIKE '%sales%' THEN 'revenue'
      WHEN line_text LIKE '%expense%' THEN 'operating_expenses'
      ELSE 'other'
    END AS metric_bucket
  FROM scoped
  WHERE line_text LIKE '%profit_and_loss%'
     OR line_text LIKE '%profit and loss%'
     OR line_text LIKE '%p&l%'
     OR line_text LIKE '%revenue%'
     OR line_text LIKE '%income%'
     OR line_text LIKE '%sales%'
     OR line_text LIKE '%expense%'
     OR line_text LIKE '%cogs%'
     OR line_text LIKE '%cost of goods%'
     OR line_text LIKE '%cost of sales%'
),
monthly AS (
  SELECT
    period_start,
    max(connection_id) AS connection_id,
    max(workbook_id) AS workbook_id,
    SUM(CASE WHEN metric_bucket = 'revenue' THEN signed_amount ELSE 0 END) AS revenue,
    SUM(CASE WHEN metric_bucket = 'cost_of_sales' THEN signed_amount ELSE 0 END) AS cost_of_sales,
    SUM(CASE WHEN metric_bucket = 'operating_expenses' THEN signed_amount ELSE 0 END) AS operating_expenses,
    SUM(CASE WHEN metric_bucket = 'other_income' THEN signed_amount ELSE 0 END) AS other_income,
    SUM(CASE WHEN metric_bucket = 'other_expense' THEN signed_amount ELSE 0 END) AS other_expense
  FROM typed
  GROUP BY 1
)
SELECT
  period_start,
  connection_id,
  workbook_id,
  revenue,
  cost_of_sales,
  operating_expenses,
  other_income,
  other_expense,
  revenue - cost_of_sales AS gross_profit,
  revenue + other_income - cost_of_sales - operating_expenses - other_expense AS net_income,
  CASE WHEN abs(revenue) > 0.000001 THEN (revenue - cost_of_sales) / revenue ELSE NULL END AS gross_margin,
  CASE WHEN abs(revenue) > 0.000001 THEN (revenue + other_income - cost_of_sales - operating_expenses - other_expense) / revenue ELSE NULL END AS net_margin
FROM monthly
ORDER BY TRY_CAST(period_start AS DATE)`,
    { previewRowLimit: 2000, timeoutMs: 90000, caller: 'CfoBriefToolHandlers:local_metric_comparisons' },
    duckDbOpts,
  )

  const monthlyRows: LocalMetricMonthlyRow[] = rows
    .map((row): LocalMetricMonthlyRow => ({
      period_start: normalizeMonthStart(row.period_start),
      connection_id: trimStr(row.connection_id),
      workbook_id: trimStr(row.workbook_id),
      revenue: num(row.revenue),
      cost_of_sales: num(row.cost_of_sales),
      operating_expenses: num(row.operating_expenses),
      other_income: num(row.other_income),
      other_expense: num(row.other_expense),
      gross_profit: num(row.gross_profit),
      net_income: num(row.net_income),
      gross_margin: Number.isFinite(Number(row.gross_margin)) ? Number(row.gross_margin) : null,
      net_margin: Number.isFinite(Number(row.net_margin)) ? Number(row.net_margin) : null,
    }))
    .filter((row) => Boolean(row.period_start))
    .sort((a, b) => a.period_start.localeCompare(b.period_start))

  if (!monthlyRows.length) {
    return {
      status: 'not_ready',
      result: {
        status: 'not_ready',
        summary: 'The local CFO statement activity table is present but has no monthly P&L rows to analyze.',
        freshness: { source: 'local_duckdb', tables: [CFO_AI_TABLES.statementMonthlyActivity] },
      },
    }
  }

  const latest = monthlyRows[monthlyRows.length - 1]
  const metrics: MetricComparisonRow[] = selectedDefs.map((def) => {
    const latestValue = metricValueForLocalRow(latest, def.id)
    const revenue = metricValueForLocalRow(latest, 'revenue')
    return {
      id: def.id,
      label: def.label,
      statement: def.statement,
      value_kind: def.value_kind,
      latest_value: latestValue,
      latest_pct_of_sales: def.value_kind === 'currency' ? comparisonPctOfSales(latestValue, revenue) : null,
      comparisons: comparisons
        .map((kind) => localComparisonForMetric(monthlyRows, def.id, kind))
        .filter((comparison): comparison is MetricComparison => comparison != null),
    }
  })

  return {
    status: 'ready',
    response: {
      workbook_id: wb,
      connection_id: trimStr(latest.connection_id),
      realm_id: '',
      as_of: latest.period_start,
      grain: 'month',
      registry_version: 'local-cfo-ai-v1',
      signature: `local_${latest.period_start}_${monthlyRows.length}_${metrics.map((m) => m.id).join('_')}`,
      comparisons,
      metrics,
      warnings: [],
    },
  }
}

type OpexBreakdownResult = Record<string, any> & {
  status: 'ready' | 'not_ready'
  standout_accounts?: Array<Record<string, any>>
}

const buildOpexBreakdown = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  asOfHint: string | null,
  args: Record<string, any>,
): Promise<OpexBreakdownResult> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    return {
      status: 'not_ready',
      summary: 'Local DuckDB is not available yet, so Opex drilldown evidence cannot be computed.',
      missing_tables: [CFO_AI_TABLES.opexMonthlyActivity],
    }
  }
  const ready = await duckTableExists(workerClient, CFO_AI_TABLES.opexMonthlyActivity, duckDbOpts)
  if (!ready) {
    return {
      status: 'not_ready',
      summary: 'CFO Opex drilldown cannot run because canonical AI semantic views are not ready yet.',
      missing_tables: [CFO_AI_TABLES.opexMonthlyActivity],
      semantic_gap: {
        required: [CFO_AI_TABLES.opexMonthlyActivity],
        message: 'Run CFO Home warmup or Update QuickBooks data so FuseGrid can build canonical local AI analytics views.',
      },
      freshness: { source: 'local_duckdb', required_tables: [CFO_AI_TABLES.opexMonthlyActivity] },
    }
  }
  const asOf = normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint)
  const effectiveAsOf = asOf || normalizeMonthStart((await runDuckRows(
    workerClient,
    `SELECT MAX(CAST(TRY_CAST(period_start AS DATE) AS VARCHAR)) AS as_of FROM ${quoteDuckIdent(CFO_AI_TABLES.opexMonthlyActivity)}`,
    { previewRowLimit: 1, timeoutMs: 30000, caller: 'CfoBriefToolHandlers:opex_max_period' },
    duckDbOpts,
  ))[0]?.as_of)
  const currentMonth = normalizeMonthStart(effectiveAsOf)
  const priorMonth = addMonths(currentMonth, -1)
  if (!currentMonth || !priorMonth) {
    return {
      status: 'not_ready',
      summary: 'CFO Opex drilldown could not determine a current and prior month from local artifacts.',
      freshness: { source: 'local_duckdb', required_tables: [CFO_AI_TABLES.opexMonthlyActivity] },
    }
  }
  const rows = await runDuckRows(
    workerClient,
    `
WITH scoped AS (
  SELECT
    account_id,
    account_label,
    category_label,
    CAST(date_trunc('month', TRY_CAST(period_start AS DATE)) AS VARCHAR) AS period_start,
    coalesce(TRY_CAST(monthly_activity AS DOUBLE), 0) AS amount,
    source_table_names
  FROM ${quoteDuckIdent(CFO_AI_TABLES.opexMonthlyActivity)}
  WHERE TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(priorMonth)} AS DATE)
    AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(addMonths(currentMonth, 1))} AS DATE)
)
SELECT
  account_id,
  account_label,
  category_label,
  SUM(CASE WHEN period_start = ${quoteDuckString(currentMonth)} THEN amount ELSE 0 END) AS current_value,
  SUM(CASE WHEN period_start = ${quoteDuckString(priorMonth)} THEN amount ELSE 0 END) AS prior_value,
  SUM(CASE WHEN period_start = ${quoteDuckString(currentMonth)} THEN amount ELSE 0 END)
    - SUM(CASE WHEN period_start = ${quoteDuckString(priorMonth)} THEN amount ELSE 0 END) AS delta_abs,
  string_agg(DISTINCT source_table_names, ',') AS source_table_names
FROM scoped
GROUP BY 1, 2, 3
HAVING ABS(delta_abs) > 0.005 OR ABS(current_value) > 0.005 OR ABS(prior_value) > 0.005
ORDER BY ABS(delta_abs) DESC
LIMIT 500`,
    { previewRowLimit: 600, timeoutMs: 90000, caller: 'CfoBriefToolHandlers:get_opex_breakdown' },
    duckDbOpts,
  )
  const accountRows = rows.map((row) => {
    const delta = num(row.delta_abs)
    return {
      account_id: trimStr(row.account_id),
      account_label: trimStr(row.account_label) || trimStr(row.account_id),
      category_id: trimStr(row.category_label) || 'Operating expenses',
      category_label: trimStr(row.category_label) || 'Operating expenses',
      current_value: num(row.current_value),
      prior_value: num(row.prior_value),
      delta_abs: delta,
      profit_effect: -delta,
      source_table_names: trimStr(row.source_table_names).split(',').filter(Boolean),
    }
  })
  const categoryMap = new Map<string, Record<string, any>>()
  for (const row of accountRows) {
    const key = row.category_id
    const existing = categoryMap.get(key) || {
      category_id: key,
      category_label: row.category_label,
      current_value: 0,
      prior_value: 0,
      delta_abs: 0,
      profit_effect: 0,
      account_count: 0,
      account_ids: [],
      source_table_names: new Set<string>(),
    }
    existing.current_value += row.current_value
    existing.prior_value += row.prior_value
    existing.delta_abs += row.delta_abs
    existing.profit_effect -= row.delta_abs
    existing.account_count += 1
    existing.account_ids.push(row.account_id)
    row.source_table_names.forEach((name: string) => existing.source_table_names.add(name))
    categoryMap.set(key, existing)
  }
  const categoryRows = Array.from(categoryMap.values())
    .map((row): Record<string, any> => ({ ...row, source_table_names: Array.from(row.source_table_names) }))
    .sort((a, b) => Math.abs(b.delta_abs) - Math.abs(a.delta_abs))
  const totalOpexDelta = categoryRows.reduce((sum, row) => sum + num(row.delta_abs), 0)
  const standoutCategories = categoryRows
    .slice(0, 2)
    .filter((row) => Math.abs(num(row.delta_abs)) >= Math.max(10000, Math.abs(totalOpexDelta) * 0.15))
  const standoutCategoryIds = new Set(standoutCategories.map((row) => row.category_id))
  const accountRowsWithPct = accountRows.map((row) => {
    const categoryDelta = num(categoryMap.get(row.category_id)?.delta_abs)
    return {
      ...row,
      percent_of_category_delta: categoryDelta === 0 ? null : row.delta_abs / categoryDelta,
    }
  })
  const standoutAccounts = accountRowsWithPct
    .filter((row) => standoutCategoryIds.has(row.category_id))
    .filter((row) => {
      const categoryDelta = Math.abs(num(categoryMap.get(row.category_id)?.delta_abs))
      return Math.abs(num(row.delta_abs)) >= Math.max(5000, categoryDelta * 0.25)
    })
    .sort((a, b) => Math.abs(num(b.delta_abs)) - Math.abs(num(a.delta_abs)))
    .slice(0, 8)
  const firstStandoutCategory = standoutCategories[0]?.category_id
  const accountChartRows = accountRowsWithPct
    .filter((row) => !firstStandoutCategory || row.category_id === firstStandoutCategory)
    .sort((a, b) => Math.abs(num(b.delta_abs)) - Math.abs(num(a.delta_abs)))
    .slice(0, 8)
  return {
    status: 'ready',
    summary: accountRows.length
      ? `Operating expenses changed ${totalOpexDelta >= 0 ? '+' : '-'}${Math.round(Math.abs(totalOpexDelta))} MoM; ${standoutCategories.length} standout categor${standoutCategories.length === 1 ? 'y' : 'ies'} and ${standoutAccounts.length} standout account(s) selected for drilldown.`
      : 'No operating expense movement was found in the local current/prior month artifacts.',
    as_of: currentMonth,
    prior_month: priorMonth,
    freshness: {
      source: 'local_duckdb',
      tables: [
        CFO_AI_TABLES.opexMonthlyActivity,
      ],
    },
    category_rows: categoryRows,
    account_rows: accountRowsWithPct,
    standout_categories: standoutCategories,
    standout_accounts: standoutAccounts,
    chart_specs: [
      {
        kind: 'bar',
        title: 'Operating expense movement by category',
        subtitle: 'Positive bars are Opex increases; red bars are Opex decreases.',
        items: categoryRows.slice(0, 7).map((row) => ({
          label: row.category_label,
          value: row.delta_abs,
          direction: row.delta_abs > 0 ? 'down' : row.delta_abs < 0 ? 'up' : 'neutral',
          note: row.delta_abs >= 0 ? 'higher expense' : 'lower expense',
        })),
      },
      {
        kind: 'bar',
        title: firstStandoutCategory
          ? `Accounts inside ${categoryRows.find((row) => row.category_id === firstStandoutCategory)?.category_label || firstStandoutCategory}`
          : 'Operating expense account movement',
        subtitle: 'Account-level current month minus prior month.',
        items: accountChartRows.map((row) => ({
          label: row.account_label,
          value: row.delta_abs,
          direction: row.delta_abs > 0 ? 'down' : row.delta_abs < 0 ? 'up' : 'neutral',
          note: row.delta_abs >= 0 ? 'higher expense' : 'lower expense',
        })),
      },
    ],
  }
}

const buildGlAnomalyDetail = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  opexBreakdown: OpexBreakdownResult | null,
): Promise<Record<string, any>> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    return {
      status: 'not_ready',
      gl_detail_ready: false,
      summary: 'GL detail is still downloading, so transaction anomaly drilldown cannot run yet.',
      missing_tables: [CFO_AI_TABLES.glDetail],
    }
  }
  // GL warms last; wait briefly for a near-miss click rather than failing. Kept
  // short so a not-yet-warmed GL drill returns quickly instead of stalling the run.
  const glReady = await awaitCfoArtifactReady(workerClient, CFO_AI_TABLES.glDetail, duckDbOpts, { timeoutMs: 1200 })
  if (!glReady) {
    return {
      status: 'not_ready',
      gl_detail_ready: false,
      summary: 'GL detail semantic view is still preparing. I can explain category and account drivers now, but transaction-level anomalies are not ready.',
      missing_tables: [CFO_AI_TABLES.glDetail],
    }
  }
  const sourceAccounts = opexBreakdown && Array.isArray(opexBreakdown.standout_accounts)
    ? opexBreakdown.standout_accounts
    : []
  const targetAccounts = sourceAccounts
    .filter((row): row is Record<string, any> => row != null && typeof row === 'object' && Boolean(trimStr(row.account_id)))
    .sort((a, b) => Math.abs(num(b.delta_abs)) - Math.abs(num(a.delta_abs)))
    .slice(0, 3)
  if (!targetAccounts.length || opexBreakdown?.status !== 'ready') {
    return {
      status: 'ready',
      gl_detail_ready: true,
      summary: 'GL detail is ready, but no material Opex account cleared the drilldown threshold.',
      anomalies: [],
      clusters: [],
      chart_specs: [],
    }
  }
  const currentMonth = normalizeMonthStart(opexBreakdown.as_of)
  const priorMonth = normalizeMonthStart(opexBreakdown.prior_month)
  const nextMonth = addMonths(currentMonth, 1)
  const accountIds = targetAccounts.map((row) => trimStr(row.account_id)).filter(Boolean)
  const accountIn = accountIds.map(quoteDuckString).join(', ')
  const rows = await runDuckRows(
    workerClient,
    `
WITH scoped AS (
  SELECT
    CAST(TRY_CAST(txn_date AS DATE) AS VARCHAR) AS txn_date,
    account_id,
    account_label,
    nullif(trim(coalesce(vendor, '')), '') AS vendor,
    nullif(trim(coalesce(customer, '')), '') AS customer,
    nullif(trim(coalesce(name, '')), '') AS name,
    trim(coalesce(memo, '')) AS memo,
    trim(coalesce(transaction_type, '')) AS transaction_type,
    trim(coalesce(document_number, '')) AS document_number,
    coalesce(TRY_CAST(amount AS DOUBLE), 0) AS amount
  FROM ${quoteDuckIdent(CFO_AI_TABLES.glDetail)}
  WHERE account_id IN (${accountIn})
    AND TRY_CAST(txn_date AS DATE) >= TRY_CAST(${quoteDuckString(priorMonth)} AS DATE)
    AND TRY_CAST(txn_date AS DATE) < TRY_CAST(${quoteDuckString(nextMonth)} AS DATE)
),
with_period AS (
  SELECT
    *,
    CASE
      WHEN TRY_CAST(txn_date AS DATE) >= TRY_CAST(${quoteDuckString(currentMonth)} AS DATE) THEN 'current'
      ELSE 'prior'
    END AS period_bucket,
    coalesce(vendor, customer, name, 'Unspecified') AS entity_label
  FROM scoped
),
account_stats AS (
  SELECT
    account_id,
    median(CASE WHEN period_bucket = 'prior' THEN abs(amount) ELSE NULL END) AS prior_median_abs,
    SUM(CASE WHEN period_bucket = 'current' THEN amount ELSE 0 END) AS current_total,
    SUM(CASE WHEN period_bucket = 'prior' THEN amount ELSE 0 END) AS prior_total
  FROM with_period
  GROUP BY 1
),
entity_clusters AS (
  SELECT
    account_id,
    entity_label,
    SUM(CASE WHEN period_bucket = 'current' THEN amount ELSE 0 END) AS current_total,
    SUM(CASE WHEN period_bucket = 'prior' THEN amount ELSE 0 END) AS prior_total,
    COUNT(CASE WHEN period_bucket = 'current' THEN 1 END) AS current_count,
    COUNT(CASE WHEN period_bucket = 'prior' THEN 1 END) AS prior_count
  FROM with_period
  GROUP BY 1, 2
),
flagged_txns AS (
  SELECT
    w.*,
    s.prior_median_abs,
    s.current_total AS account_current_total,
    s.prior_total AS account_prior_total,
    s.current_total - s.prior_total AS account_delta,
    CASE
      WHEN abs(w.amount) >= abs(s.current_total - s.prior_total) * 0.20 AND abs(s.current_total - s.prior_total) >= 1 THEN 'single transaction >20% of account delta'
      WHEN s.prior_median_abs IS NOT NULL AND s.prior_median_abs > 0 AND abs(w.amount) > 2 * s.prior_median_abs THEN 'transaction >2x prior median'
      ELSE ''
    END AS anomaly_reason
  FROM with_period w
  JOIN account_stats s USING (account_id)
  WHERE w.period_bucket = 'current'
),
unioned AS (
  SELECT
    'txn' AS row_kind,
    txn_date,
    account_id,
    account_label,
    vendor,
    customer,
    name,
    memo,
    transaction_type,
    document_number,
    amount,
    period_bucket,
    entity_label,
    prior_median_abs,
    account_current_total,
    account_prior_total,
    account_delta,
    anomaly_reason
  FROM flagged_txns
  WHERE anomaly_reason <> ''
  UNION ALL
  SELECT
    'cluster' AS row_kind,
    NULL AS txn_date,
    c.account_id,
    NULL AS account_label,
    NULL AS vendor,
    NULL AS customer,
    c.entity_label AS name,
    NULL AS memo,
    NULL AS transaction_type,
    NULL AS document_number,
    c.current_total - c.prior_total AS amount,
    'current' AS period_bucket,
    c.entity_label AS entity_label,
    s.prior_median_abs,
    s.current_total AS account_current_total,
    s.prior_total AS account_prior_total,
    s.current_total - s.prior_total AS account_delta,
    CASE
      WHEN c.prior_count = 0 AND abs(c.current_total) > 5000 THEN 'new vendor/name over $5K'
      WHEN abs(c.current_total - c.prior_total) > 5000 THEN 'vendor/name cluster increased >$5K'
      ELSE ''
    END AS anomaly_reason
  FROM entity_clusters c
  JOIN account_stats s USING (account_id)
  WHERE (c.prior_count = 0 AND abs(c.current_total) > 5000)
     OR abs(c.current_total - c.prior_total) > 5000
)
SELECT
  *
FROM unioned
ORDER BY abs(amount) DESC
LIMIT 80`,
    { previewRowLimit: 100, timeoutMs: 120000, caller: 'CfoBriefToolHandlers:get_gl_anomaly_detail' },
    duckDbOpts,
  )
  const accountById = new Map(targetAccounts.map((row) => [trimStr(row.account_id), row]))
  const enriched = rows.map((row) => {
    const account = accountById.get(trimStr(row.account_id)) || {}
    return {
      account_id: trimStr(row.account_id),
      account_label: trimStr(account.account_label) || trimStr(row.account_label) || trimStr(row.account_id),
      txn_date: trimStr(row.txn_date) || null,
      vendor_name: trimStr(row.entity_label || row.vendor || row.customer || row.name) || 'Unspecified',
      memo: trimStr(row.memo),
      txn_type: trimStr(row.transaction_type),
      document_number: trimStr(row.document_number),
      amount: num(row.amount),
      reason: trimStr(row.anomaly_reason),
      row_kind: trimStr(row.row_kind),
      account_current_total: num(row.account_current_total),
      account_prior_total: num(row.account_prior_total),
      account_delta: num(row.account_delta),
      source_table_names: [CFO_AI_TABLES.glDetail],
    }
  })
  const anomalies = enriched.filter((row) => row.row_kind === 'txn').slice(0, 12)
  const clusters = enriched.filter((row) => row.row_kind === 'cluster').slice(0, 8)
  const primaryAccount = targetAccounts[0]
  const primaryRows = [...anomalies, ...clusters].filter((row) => row.account_id === trimStr(primaryAccount.account_id))
  return {
    status: 'ready',
    gl_detail_ready: true,
    summary: primaryRows.length
      ? `GL detail found ${primaryRows.length} transaction/cluster anomaly candidate(s) for ${trimStr(primaryAccount.account_label) || primaryAccount.account_id}.`
      : `GL detail was checked for ${targetAccounts.length} material Opex account(s); no transaction cleared the anomaly thresholds.`,
    account_id: trimStr(primaryAccount.account_id),
    account_label: trimStr(primaryAccount.account_label) || trimStr(primaryAccount.account_id),
    current_total: num(primaryAccount.current_value),
    prior_total: num(primaryAccount.prior_value),
    delta: num(primaryAccount.delta_abs),
    anomalies,
    clusters,
    chart_specs: [
      {
        kind: 'bar',
        title: 'GL anomaly candidates by amount',
        subtitle: 'Current-month transactions and vendor/name clusters flagged locally.',
        items: [...anomalies, ...clusters].slice(0, 6).map((row) => ({
          label: row.vendor_name,
          value: row.amount,
          direction: row.amount > 0 ? 'down' : row.amount < 0 ? 'up' : 'neutral',
          note: row.reason,
        })),
      },
      {
        kind: 'table',
        title: 'Transaction-level anomaly detail',
        subtitle: 'Date, vendor/name, memo/type, amount, and rule that flagged it.',
        columns: [
          { key: 'date', label: 'Date' },
          { key: 'vendor', label: 'Vendor/name' },
          { key: 'memo', label: 'Memo / type' },
          { key: 'amount', label: 'Amount', align: 'right' },
          { key: 'why', label: 'Why flagged' },
        ],
        rows: [...anomalies, ...clusters].slice(0, 8).map((row) => ({
          date: row.txn_date || 'Cluster',
          vendor: row.vendor_name,
          memo: row.memo || row.txn_type || row.document_number || '—',
          amount: row.amount,
          why: row.reason,
        })),
      },
    ],
  }
}

export type CfoBriefToolHandlerContext = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  duckDbOpts?: Record<string, any> | null
  workbookId: string | null | undefined
  /**
   * Live comparison snapshot the Brief already rendered for its KPI cards.
   * Used only for lightweight context such as the current as-of hint; agent
   * evidence tools read from local CFO artifact views.
   */
  getComparisons?: () => MetricComparisonResponse | null
}

/**
 * Build the Brief's surface-tool handler bundle. Each handler routes to local
 * CFO artifact views and returns a `{ summary, ... }` shape so `useCfoAgent`
 * records an `ok:true` evidence row the model can cite.
 */
export function buildCfoBriefToolHandlers(ctx: CfoBriefToolHandlerContext): CfoAgentToolHandlers {
  let lastOpexBreakdown: OpexBreakdownResult | null = null
  const requireWorkbook = (tool: string): string => {
    const wb = trimStr(ctx.workbookId)
    if (!wb) throw new Error(`${tool} requires an active workbook id.`)
    return wb
  }

  // Resolve a comparison response for the requested metrics + as-of from the
  // warmed local CFO AI semantic views. This remains available only for local
  // artifact drilldowns; governed page copilots use getGovernedMetricEvidence.
  const resolveComparisons = async (
    args: Record<string, any>,
    metricIds: string[],
  ): Promise<LocalComparisonResolution> => {
    return await buildLocalMetricComparisons(ctx.workerClient, ctx.duckDbOpts, ctx.workbookId, args, metricIds)
  }

  const resolveProfitDriverComparisons = async (args: Record<string, any>): Promise<MetricComparisonResponse> => {
    const resolved = await resolveComparisons(args, [])
    if (resolved.status !== 'ready') throw new Error(trimStr(resolved.result.summary) || 'Local comparison artifacts are not ready.')
    return resolved.response
  }

  const getGovernedMetricEvidence = async (args: Record<string, any>) => {
    const workbookId = requireWorkbook('get_cfo_metric_evidence')
    const metricIds = requestedMetricIds(args).slice(0, 12)
    const comparisons = requestedComparisonKinds(args)
    const asOf = trimStr(args?.as_of || args?.asOf || args?.period) || undefined
    const response = await ctx.appApi.getQuickBooksMetricComparisons(workbookId, {
      asOf,
      grain: 'month',
      metrics: metricIds.length ? metricIds : undefined,
      comparisons: comparisons.length ? comparisons : undefined,
    })
    const selectedMetrics = (metricIds.length
      ? response.metrics.filter((metric) => metricIds.includes(metric.id))
      : response.metrics
    ).slice(0, 12)
    return {
      status: 'ready',
      summary: `Fetched governed CFO metric evidence for ${selectedMetrics.length} metric(s) from the server comparison contract.`,
      contract_id: 'metric_comparisons',
      filters: {
        as_of: asOf ?? response.as_of ?? null,
        grain: response.grain,
        metrics: metricIds,
        comparisons: comparisons.length ? comparisons : response.comparisons,
      },
      freshness: {
        source: 'server_metric_comparisons',
        freshness_token: response.signature,
        readiness: 'ready',
        rebuild_state: 'ready',
        source_artifact_ids: [response.signature],
      },
      numeric_parity: {
        rule: 'AI CFO metric answers must use the same metric comparison response as CFO Home KPI tiles, ratios context, and statement-derived exports for the same workbook, period, metric, and signature.',
        workbook_id: response.workbook_id,
        freshness_token: response.signature,
      },
      row_count: response.metrics.length,
      returned_row_count: selectedMetrics.length,
      workbook_id: response.workbook_id,
      connection_id: response.connection_id,
      realm_id: response.realm_id,
      as_of: response.as_of,
      grain: response.grain,
      registry_version: response.registry_version,
      signature: response.signature,
      comparisons: response.comparisons,
      metrics: selectedMetrics,
      warnings: response.warnings || [],
    }
  }

  const getGovernedGlSummaryEvidence = async (args: Record<string, any>) => {
    const workbookId = requireWorkbook('search_cfo_gl_summary')
    const asOf = trimStr(args?.as_of || args?.asOf || args?.period) || undefined
    const statementType = trimStr(args?.statement_type || args?.statementType || args?.statement) || undefined
    const layoutNodeId = trimStr(args?.layout_node_id || args?.layoutNodeId || args?.account_id || args?.accountId) || undefined
    const search = trimStr(args?.search || args?.query || args?.vendor || args?.memo) || undefined
    const limit = boundedEvidenceLimit(args?.limit ?? args?.row_limit ?? args?.rowLimit)
    const response = await ctx.appApi.getQuickBooksGlSummary(workbookId, {
      asOf,
      statementType,
      layoutNodeId,
      search,
      limit,
    })
    const contract = response?.contract || null
    const returnedRowCount = Array.isArray(response.rows) ? response.rows.length : 0
    return {
      status: trimStr(contract?.readiness) || 'ready',
      summary: `Fetched bounded governed GL summary evidence (${response.row_count || 0} matching row(s), returned ${returnedRowCount}, scanned ${response.scan_row_count ?? response.row_count ?? 0}).`,
      contract_id: 'gl_summary',
      filters: {
        as_of: asOf ?? response.as_of ?? null,
        statement_type: statementType ?? response.statement_type ?? null,
        layout_node_id: layoutNodeId ?? null,
        search: search ?? null,
        limit,
      },
      freshness: cfoContractEvidenceMetadata(contract, 'server_cfo_page_contract'),
      numeric_parity: {
        rule: 'AI CFO, UI GL summary, and GL summary export must use this same server contract for the same workbook, filters, and freshness token.',
        workbook_id: response.workbook_id,
        freshness_token: contract?.freshness_token ?? null,
      },
      row_count: response.row_count ?? 0,
      scan_row_count: response.scan_row_count ?? response.row_count ?? 0,
      returned_row_count: returnedRowCount,
      returned_row_limit: response.returned_row_limit ?? limit,
      limited: Boolean(response.limited),
      totals: response.totals || {},
      top_accounts: response.top_accounts || [],
      top_vendors: response.top_vendors || [],
      top_classes: response.top_classes || [],
      rows: Array.isArray(response.rows) ? response.rows.slice(0, limit) : [],
      warnings: Array.isArray(response.warnings) ? response.warnings : contract?.warnings || [],
      contract: response,
    }
  }

  return {
    get_client_context: async (args: Record<string, any>) => {
      const workbookId = requireWorkbook('get_client_context')
      const asOf = trimStr(args?.as_of || args?.asOf || args?.period) || undefined
      const sourceVersion = trimStr(args?.source_version || args?.sourceVersion) || undefined
      const response = await ctx.appApi.getPlatformClientContext(workbookId, { asOf, sourceVersion })
      return {
        status: 'ready',
        summary: 'Fetched the versioned governed client context packet.',
        ...response,
      }
    },

    investigate_material_variance: async (args: Record<string, any>) => {
      const workbookId = requireWorkbook('investigate_material_variance')
      const input = {
        comparison: trimStr(args?.comparison) || 'mom',
        metric_ids: requestedMetricIds(args).slice(0, 20),
        as_of: trimStr(args?.as_of || args?.asOf || args?.period) || undefined,
        source_version: trimStr(args?.source_version || args?.sourceVersion) || undefined,
        expected_freshness_token: trimStr(args?.expected_freshness_token || args?.expectedFreshnessToken) || undefined,
      }
      const response = await ctx.appApi.invokePlatformCapability(
        'investigate_material_variance',
        workbookId,
        input,
      )
      return {
        status: 'ready',
        summary: 'Fetched a prepared, single-version material-variance investigation.',
        ...response,
      }
    },

    create_financial_artifact: async (args: Record<string, any>) => {
      const workbookId = requireWorkbook('create_financial_artifact')
      const response = await ctx.appApi.invokePlatformCapability(
        'create_financial_artifact',
        workbookId,
        {
          title: trimStr(args?.title) || undefined,
          comparison: trimStr(args?.comparison) || 'mom',
          metric_ids: requestedMetricIds(args).slice(0, 20),
          output_formats: Array.isArray(args?.output_formats) ? args.output_formats.slice(0, 4) : [],
          as_of: trimStr(args?.as_of || args?.asOf || args?.period) || undefined,
          source_version: trimStr(args?.source_version || args?.sourceVersion) || undefined,
          expected_freshness_token: trimStr(args?.expected_freshness_token || args?.expectedFreshnessToken) || undefined,
        },
      )
      return {
        status: 'ready',
        summary: 'Compiled a governed FinancialArtifactSpec from the versioned client context.',
        ...response,
      }
    },

    // Governed server contracts are the AI CFO's page-level map: each contract
    // tells the model which render/API shape is authoritative for a page, what
    // engines may back it, and where artifact freshness/readiness comes from.
    list_cfo_page_contracts: async () => {
      const workbookId = requireWorkbook('list_cfo_page_contracts')
      const catalog = await ctx.appApi.getQuickBooksCfoArtifactCatalog(workbookId)
      const pageContracts = Array.isArray(catalog.page_contracts) ? catalog.page_contracts : []
      return {
        status: pageContracts.length ? 'ready' : 'not_ready',
        summary: pageContracts.length
          ? `CFO Core exposes ${pageContracts.length} governed page contract(s) for the AI CFO and UI.`
          : 'No CFO page contracts were returned for this workbook.',
        contract: catalog.contract,
        page_contracts: pageContracts,
        query_engines: catalog.query_engines || [],
        artifact_count: Array.isArray(catalog.artifacts) ? catalog.artifacts.length : 0,
        freshness: cfoContractEvidenceMetadata(catalog.contract, 'server_cfo_artifact_catalog'),
      }
    },

    // Fetch a single governed page contract through the typed Rust API surface.
    // This is intentionally a small allowlist, not a browser-supplied URL or SQL pipe.
    get_cfo_page_contract: async (args: Record<string, any>) => {
      const workbookId = requireWorkbook('get_cfo_page_contract')
      const contractId = trimStr(args?.contract_id || args?.page || args?.id).toLowerCase()
      const asOf = trimStr(args?.as_of || args?.asOf || args?.period) || undefined
      const grain = 'month' as const
      let contract: any
      if (contractId === 'cfo_home' || contractId === 'home') {
        contract = await ctx.appApi.getQuickBooksCfoHome(workbookId, { asOf, grain })
      } else if (contractId === 'statements' || contractId === 'financials') {
        contract = await ctx.appApi.getQuickBooksCfoStatementsRender(workbookId, { asOf, grain })
      } else if (contractId === 'ratios') {
        contract = await ctx.appApi.getQuickBooksRatios(workbookId, { asOf, grain })
      } else if (contractId === 'ar' || contractId === 'accounts_receivable') {
        contract = await ctx.appApi.getQuickBooksArAnalysis(workbookId, { asOf, grain })
      } else if (contractId === 'forecast') {
        const statementType = trimStr(args?.statement_type || args?.statementType || args?.statement) || 'cash_flow'
        const valueCase = trimStr(args?.value_case || args?.valueCase || args?.case) || 'expected'
        const scenarioKey = trimStr(args?.scenario_key || args?.scenarioKey || args?.scenario) || undefined
        const packageId = trimStr(args?.package_id || args?.packageId) || undefined
        const futureMonths = boundedEvidenceLimit(args?.future_months ?? args?.futureMonths, 12, 60)
        const limit = boundedEvidenceLimit(args?.limit ?? args?.row_limit ?? args?.rowLimit, 200, 1000)
        contract = await ctx.appApi.getQuickBooksFpaMlStatementForecast(workbookId, {
          package_id: packageId,
          scenario_key: scenarioKey,
          grain,
          statement_type: statementType,
          value_case: valueCase,
          as_of_date: asOf,
          future_months: futureMonths,
          limit,
        })
      } else if (contractId === 'valuation') {
        contract = await ctx.appApi.getQuickBooksValuation(workbookId, { asOf, grain })
      } else if (contractId === 'gl_summary' || contractId === 'gl') {
        return await getGovernedGlSummaryEvidence(args)
      } else if (contractId === 'cash_planning' || contractId === 'cash') {
        contract = await ctx.appApi.getQuickBooksCashPlanning(workbookId, { as_of_date: asOf })
      } else {
        return {
          status: 'not_ready',
          summary: `Unknown CFO page contract "${contractId || 'unspecified'}". Use list_cfo_page_contracts first, then request one of its contract ids.`,
          contract_id: contractId || null,
        }
      }
      const readiness = trimStr(contract?.contract?.readiness)
      return {
        status: readiness || (contract ? 'ready' : 'not_ready'),
        summary: `Fetched governed CFO page contract "${contractId}".`,
        contract_id: contractId,
        freshness: cfoContractEvidenceMetadata(contract?.contract, 'server_cfo_page_contract'),
        contract,
      }
    },

    // Governed GL drilldown/search path for the AI CFO. The tool accepts only
    // typed filters and delegates to the same Rust page contract the GL UI and
    // CSV export use; it intentionally has no SQL argument.
    search_cfo_gl_summary: async (args: Record<string, any>) => {
      return await getGovernedGlSummaryEvidence(args)
    },

    // Server-first metric evidence for the AI CFO. This is the common-path
    // contract-aligned metric lookup; local DuckDB metric tools remain for
    // explicit warmed-artifact drilldowns and desktop/ad hoc workflows.
    get_cfo_metric_evidence: async (args: Record<string, any>) => {
      return await getGovernedMetricEvidence(args)
    },

    // Discover the workbook's prepared local data mart. Mirrors Growth
    // Analysis's `list_agent_artifacts`, but checks the warmed DuckDB views
    // directly instead of asking the server manifest endpoint.
    list_agent_artifacts: async () => {
      requireWorkbook('list_agent_artifacts')
      if (!ctx.workerClient || typeof ctx.workerClient.duckdb_run_sql !== 'function') {
        return localArtifactsNotReady('Local DuckDB is not available yet, so agent artifacts cannot be listed.')
      }
      const artifactReadiness = await buildCfoDrilldownArtifactReadiness(ctx.workerClient, ctx.duckDbOpts)
      const artifacts = artifactReadiness
        .filter((item) => item.status === 'ready')
        .map((item) => ({
          id: item.table,
          view_id: item.view_id,
          name: item.label,
          table_name: item.table,
          row_count: item.row_count ?? 0,
          source: 'local_duckdb',
          source_artifacts: item.source_artifacts || [],
        }))
      return {
        status: artifacts.length ? 'ready' : 'not_ready',
        summary: artifacts.length
          ? `Local CFO agent data mart has ${artifacts.length} warmed semantic table(s).`
          : 'No local CFO agent semantic tables are warmed yet.',
        cache_level: artifacts.some((table) => table.id === CFO_AI_TABLES.glDetail) ? 'detail' : 'summary',
        gl_detail_ready: artifacts.some((table) => table.id === CFO_AI_TABLES.glDetail),
        freshness: { source: 'local_duckdb', tables: artifacts.map((table) => table.table_name) },
        artifact_readiness: artifactReadiness,
        missing_views: artifactReadiness.filter((item) => item.status !== 'ready').map((item) => item.table),
        artifacts,
      }
    },

    // Definition + lineage for a metric: the canonical comparison row (label,
    // statement, value kind, latest value) enriched with the ratio library's
    // plain-language formula/interpretation when the id is a ratio.
    get_metric_definition: async (args) => {
      const ids = requestedMetricIds(args)
      const targetId = ids[0] || ''
      const resolved = await resolveComparisons(args, ids)
      if (resolved.status !== 'ready') return resolved.result
      const resp = resolved.response
      const row = targetId ? findMetricRow(resp, targetId) : resp.metrics[0] || null
      if (row) {
        const localDef = LOCAL_METRIC_DEF_BY_ID.get(row.id)
        const formula = localDef?.formula || null
        const interpretation = localDef?.interpretation || null
        return {
          summary: `${row.label} (${row.statement}) latest value is ${row.latest_value ?? 'n/a'}${formula ? `; formula: ${formula}` : ''}.`,
          metric: {
            id: row.id,
            label: row.label,
            statement: row.statement,
            value_kind: row.value_kind,
            latest_value: row.latest_value,
            latest_pct_of_sales: row.latest_pct_of_sales,
            formula,
            interpretation,
          },
        }
      }
      return {
        status: 'not_ready',
        summary: `No local metric definition is available for "${targetId || 'the requested metric'}" in the warmed CFO artifact set.`,
        available_metrics: resp.metrics.map((m) => ({ id: m.id, label: m.label })),
        freshness: { source: 'local_duckdb', tables: [CFO_AI_TABLES.statementMonthlyActivity] },
      }
    },

    // Rolling trajectory for a metric from the comparison engine windows.
    get_metric_trend: async (args) => {
      const ids = requestedMetricIds(args)
      const targetId = ids[0] || ''
      const resolved = await resolveComparisons(args, ids)
      if (resolved.status !== 'ready') return resolved.result
      const resp = resolved.response
      const row = targetId ? findMetricRow(resp, targetId) : resp.metrics[0] || null
      if (!row) {
        return {
          summary: `No trend is available for "${targetId || 'the requested metric'}" in this workbook.`,
          available_metrics: resp.metrics.map((m) => ({ id: m.id, label: m.label })),
        }
      }
      const { labels, trend } = buildTrend(row)
      const mom = pickComparison(row, 'mom')
      return {
        summary: `${row.label} trend has ${trend.length} point(s); latest ${row.latest_value ?? 'n/a'} as of ${resp.as_of}.`,
        metric_id: row.id,
        label: row.label,
        basis: 'comparison_windows',
        as_of: resp.as_of,
        labels,
        trend,
        current: row.latest_value,
        prior: mom?.prior_value ?? null,
      }
    },

    // Driver of a metric's change: the windowed deltas ($ and % of sales) from
    // the comparison engine. The model narrates the magnitude/direction; the
    // numbers are the canonical ones the Brief shows.
    explain_metric_change: async (args) => {
      const ids = requestedMetricIds(args)
      const targetId = ids[0] || ''
      const resolved = await resolveComparisons(args, ids)
      if (resolved.status !== 'ready') return resolved.result
      const resp = resolved.response
      const row = targetId ? findMetricRow(resp, targetId) : findMetricRow(resp, 'net_income') || resp.metrics[0] || null
      if (!row) {
        return {
          summary: `No change detail is available for "${targetId || 'the requested metric'}" in this workbook.`,
          available_metrics: resp.metrics.map((m) => ({ id: m.id, label: m.label })),
        }
      }
      const comparisonKind = requestedComparisonKind(args, 'mom')
      const movement = summarizeMovement(pickComparison(row, comparisonKind))
      const bridge = row.comparisons
        .filter((c) => COMPARISON_KINDS.has(c.kind))
        .map((c) => summarizeMovement(c))
        .filter(Boolean)
      const deltaText =
        movement?.delta_abs != null
          ? `${row.label} moved ${movement.delta_abs >= 0 ? '+' : ''}${movement.delta_abs} ${movement.label || comparisonKind} (${movement.delta_pct != null ? `${(movement.delta_pct * 100).toFixed(1)}%` : 'n/a'}).`
          : `${row.label} has no complete ${comparisonKind} movement.`
      return {
        summary: deltaText,
        metric_id: row.id,
        label: row.label,
        statement: row.statement,
        comparison: comparisonKind,
        as_of: resp.as_of,
        movement,
        bridge,
        drivers:
          row.id === 'net_income' || row.label.toLowerCase().includes('profit')
            ? buildProfitDrivers(await resolveProfitDriverComparisons(args), comparisonKind)
            : [],
        latest_value: row.latest_value,
        latest_pct_of_sales: row.latest_pct_of_sales,
        selected_waterfall_delta: Number.isFinite(Number(args.selected_waterfall_delta))
          ? Number(args.selected_waterfall_delta)
          : null,
        selected_net_income_effect: Number.isFinite(Number(args.selected_net_income_effect))
          ? Number(args.selected_net_income_effect)
          : null,
        source_component: trimStr(args.source_component),
      }
    },

    // Statement context for a metric: its statement classification, latest value
    // / % of sales, and the windowed comparison rows. (Line-level GL drilldown
    // via `getQuickBooksFpaStatementDrilldown` needs a layout node id mapping
    // the Brief does not yet carry; that enrichment is Phase 2 in the plan. We
    // return the grounded statement-layer numbers rather than guessing a node
    // id, which would risk returning the wrong rows.)
    get_statement_detail: async (args) => {
      const ids = requestedMetricIds(args)
      const targetId = ids[0] || ''
      const resolved = await resolveComparisons(args, ids)
      if (resolved.status !== 'ready') return resolved.result
      const resp = resolved.response
      const row = targetId ? findMetricRow(resp, targetId) : null
      if (row) {
        const comparisonKind = requestedComparisonKind(args, 'mom')
        const comparisonLabel = pickComparison(row, comparisonKind)?.label || comparisonKind
        const profitDrivers =
          row.id === 'net_income' || row.label.toLowerCase().includes('profit')
            ? buildProfitDrivers(await resolveProfitDriverComparisons(args), comparisonKind)
            : []
        return {
          summary: profitDrivers.length
            ? `${row.label} sits on the ${row.statement} statement; latest ${row.latest_value ?? 'n/a'} (${row.latest_pct_of_sales != null ? `${(row.latest_pct_of_sales * 100).toFixed(1)}% of sales` : 'n/a % of sales'}). Top ${comparisonLabel} drivers are ${profitDrivers.slice(0, 3).map((driver) => `${driver.label} (${Number(driver.profit_effect || 0) >= 0 ? 'helped' : 'hurt'} ${Math.round(Math.abs(Number(driver.profit_effect || 0)))})`).join(', ')}.`
            : `${row.label} sits on the ${row.statement} statement; latest ${row.latest_value ?? 'n/a'} (${row.latest_pct_of_sales != null ? `${(row.latest_pct_of_sales * 100).toFixed(1)}% of sales` : 'n/a % of sales'}).`,
          metric_id: row.id,
          label: row.label,
          statement: row.statement,
          latest_value: row.latest_value,
          latest_pct_of_sales: row.latest_pct_of_sales,
          comparisons: row.comparisons.map((c) => summarizeMovement(c)),
          driver_basis: profitDrivers.length ? `profit_and_loss_metric_${comparisonKind}_delta_abs_with_expense_sign_adjustment` : null,
          drivers: profitDrivers,
        }
      }
      return {
        summary: `Statement lines for ${resp.metrics.length} metric(s) as of ${resp.as_of}.`,
        as_of: resp.as_of,
        lines: resp.metrics.map((m) => ({
          metric_id: m.id,
          label: m.label,
          statement: m.statement,
          latest_value: m.latest_value,
          latest_pct_of_sales: m.latest_pct_of_sales,
        })),
      }
    },

    compare_periods: async (args) => {
      const evidence = await getGovernedMetricEvidence(args)
      const resp = evidence as any
      return {
        ...evidence,
        summary: `Governed server period comparisons for ${resp.metrics?.length || 0} metric(s) as of ${resp.as_of || 'the selected period'} (${(resp.comparisons || []).join(', ')}).`,
        contract_id: 'metric_comparisons',
      }
    },

    get_insights: async (args) => {
      const workbookId = requireWorkbook('get_insights')
      const asOf = trimStr(args?.as_of || args?.asOf || args?.period) || undefined
      const resp = await ctx.appApi.getQuickBooksInsights(workbookId, { asOf, grain: 'month' })
      return {
        ...resp,
        status: 'ready',
        summary: `${resp.attention_count} governed server insight card(s) need attention of ${resp.cards.length} total as of ${resp.as_of}.`,
        contract_id: 'insights',
        filters: {
          as_of: asOf ?? resp.as_of ?? null,
          grain: resp.grain,
        },
        freshness: {
          source: 'server_insights',
          freshness_token: resp.signature,
          readiness: 'ready',
          rebuild_state: 'ready',
          source_artifact_ids: [resp.signature],
        },
        workbook_id: resp.workbook_id,
        connection_id: resp.connection_id,
        realm_id: resp.realm_id,
      }
    },

    run_drilldown_recipe: async (args) => {
      const live = ctx.getComparisons?.() || null
      return await runCfoDrilldownRecipe(ctx.workerClient, ctx.duckDbOpts, live?.as_of || null, args, {
        appApi: ctx.appApi,
        workbookId: ctx.workbookId,
      })
    },

    get_opex_breakdown: async (args) => {
      const live = ctx.getComparisons?.() || null
      const manifestResult = await runCfoDrilldownRecipe(ctx.workerClient, ctx.duckDbOpts, live?.as_of || null, {
        ...args,
        metric_id: args.metric_id ?? args.metricId ?? 'operating_expenses',
        recipe_id: 'opex_category_account_movement',
      }, {
        appApi: ctx.appApi,
        workbookId: ctx.workbookId,
      })
      const result: OpexBreakdownResult = manifestResult.status === 'ready'
        ? {
            ...manifestResult,
            status: 'ready' as const,
            standout_categories: manifestResult.standout_categories || [],
            standout_accounts: manifestResult.standout_accounts || [],
          }
        : await buildOpexBreakdown(ctx.workerClient, ctx.duckDbOpts, live?.as_of || null, args)
      lastOpexBreakdown = result
      return result
    },

    get_gl_anomaly_detail: async () => {
      return await buildGlAnomalyDetail(ctx.workerClient, ctx.duckDbOpts, lastOpexBreakdown)
    },
  }
}
